// ─────────────────────────────────────────────────────────────────────────────
// Item availability — schedule check (Q1 / E1)
// ─────────────────────────────────────────────────────────────────────────────
// Returns true iff the item is currently available in the location's TZ.
// Backward compatible: missing schedule = always on.
// ─────────────────────────────────────────────────────────────────────────────

export interface ItemAvailability {
  available_from?: string | null      // 'HH:MM:SS' or 'HH:MM'
  available_until?: string | null     // 'HH:MM:SS' or 'HH:MM'
  available_days?: number[] | null    // 0=Sun … 6=Sat
}

// ─────────────────────────────────────────────────────────────────────────────
// Schedule presence + human label
// ─────────────────────────────────────────────────────────────────────────────
// Used by the storefronts (POS / online / kiosk) to SHOW scheduled items always
// but tag them with when they're sold (e.g. "Solo Mié", "Mié · 7:00–11:00"),
// instead of silently hiding them. `isItemAvailableNow` still decides whether the
// item can be added to an order right now.
// ─────────────────────────────────────────────────────────────────────────────

/** True when the item has ANY day-of-week or time-of-day restriction configured. */
export function itemHasSchedule(item: ItemAvailability): boolean {
  const noFrom = item.available_from == null || item.available_from === ""
  const noUntil = item.available_until == null || item.available_until === ""
  const noDays = !Array.isArray(item.available_days) || item.available_days.length === 0
  return !(noFrom && noUntil && noDays)
}

type AvailabilityLocale = "es" | "en"

// index 0 = Sunday, matching the available_days convention.
const WEEKDAY_ABBR: Record<AvailabilityLocale, string[]> = {
  es: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
}

const WORDS: Record<AvailabilityLocale, { only: string; from: string; until: string }> = {
  es: { only: "Solo", from: "desde", until: "hasta" },
  en: { only: "Only", from: "from", until: "until" },
}

// "07:00:00" → "7:00", "07:30" → "7:30". Drops seconds and the leading hour zero.
function stripSeconds(t: string): string {
  const [hStr, mStr = "00"] = t.split(":")
  const h = parseInt(hStr, 10)
  if (Number.isNaN(h)) return t
  return `${h}:${mStr.slice(0, 2).padStart(2, "0")}`
}

/**
 * Short human label describing WHEN the item is sold, e.g. "Solo Mié",
 * "Lun, Mié, Vie", "Mié · 7:00–11:00", "desde 18:00". Returns null when the item
 * has no schedule (available all day, every day). Days: 0=Sun … 6=Sat.
 */
export function availabilityLabel(
  item: ItemAvailability,
  locale: AvailabilityLocale = "es",
): string | null {
  if (!itemHasSchedule(item)) return null
  const lang = locale === "en" ? "en" : "es"
  const days = WEEKDAY_ABBR[lang]
  const words = WORDS[lang]
  const parts: string[] = []

  const rawDays = Array.isArray(item.available_days) ? item.available_days : []
  const uniqueDays = Array.from(new Set(rawDays)).filter((d) => d >= 0 && d <= 6)
  if (uniqueDays.length > 0 && uniqueDays.length < 7) {
    // Order Mon→Sun so the list reads naturally (week starts on Monday here).
    const ordered = [1, 2, 3, 4, 5, 6, 0].filter((d) => uniqueDays.includes(d))
    const labels = ordered.map((d) => days[d])
    parts.push(labels.length === 1 ? `${words.only} ${labels[0]}` : labels.join(", "))
  }

  const from = item.available_from ? stripSeconds(item.available_from) : null
  const until = item.available_until ? stripSeconds(item.available_until) : null
  if (from && until) parts.push(`${from}–${until}`)
  else if (from) parts.push(`${words.from} ${from}`)
  else if (until) parts.push(`${words.until} ${until}`)

  return parts.length > 0 ? parts.join(" · ") : null
}

export function isItemAvailableNow(
  item: ItemAvailability,
  timeZone: string = "UTC",
  nowOverride?: Date,
): boolean {
  const noFrom = item.available_from == null || item.available_from === ""
  const noUntil = item.available_until == null || item.available_until === ""
  const noDays = !Array.isArray(item.available_days) || item.available_days.length === 0
  if (noFrom && noUntil && noDays) return true

  const now = nowOverride ?? new Date()
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now)

  const weekdayShort = parts.find(p => p.type === "weekday")?.value ?? ""
  const hourStr = parts.find(p => p.type === "hour")?.value ?? "00"
  const minuteStr = parts.find(p => p.type === "minute")?.value ?? "00"

  const WEEKDAY_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  const weekday = WEEKDAY_INDEX[weekdayShort]

  if (!noDays && item.available_days && !item.available_days.includes(weekday)) return false

  function toMinutes(t: string): number {
    const [h, m] = t.split(":").map((s) => parseInt(s, 10))
    return h * 60 + (m || 0)
  }
  const nowMinutes = parseInt(hourStr, 10) * 60 + parseInt(minuteStr, 10)
  const fromM = item.available_from ? toMinutes(item.available_from) : null
  const untilM = item.available_until ? toMinutes(item.available_until) : null

  if (fromM != null && untilM != null) {
    if (fromM <= untilM) {
      if (nowMinutes < fromM || nowMinutes >= untilM) return false
    } else {
      if (nowMinutes < fromM && nowMinutes >= untilM) return false
    }
  } else if (fromM != null) {
    if (nowMinutes < fromM) return false
  } else if (untilM != null) {
    if (nowMinutes >= untilM) return false
  }
  return true
}
